/**
 * The caption on a reel page. It is a text object in the page JSON so the
 * existing MP4 export draws it. No Fabric import: unit tests stay node-only.
 */

import type { LayerAnimation, ProjectPage } from '@canvas/shared-types';
import { presetDef } from '@/data/animationPresets';
import { REEL_PLATFORMS } from '@/data/reelPlatforms';

/** Mark on the text object so a later line replaces it and leaves artwork. */
export const REEL_CAPTION_KEY = 'craftaraReelCaption';

/** How the caption enters. Still means no motion. */
export type ReelCaptionMotion = 'none' | 'fade' | 'slide' | 'rise';

const MOTION_PRESET: Record<Exclude<ReelCaptionMotion, 'none'>, 'fade-in' | 'slide-in' | 'rise'> = {
  fade: 'fade-in',
  slide: 'slide-in',
  rise: 'rise',
};

/** Bottom inset for a 9:16 master. The deepest platform chrome wins. */
export function reelCaptionInset(): number {
  const vertical = REEL_PLATFORMS.filter((platform) => platform.height > platform.width);
  const bottoms = vertical.map((platform) => platform.safeBottom);
  return bottoms.length ? Math.max(...bottoms) : 0.16;
}

function isReelCaption(obj: Record<string, unknown>): boolean {
  return obj[REEL_CAPTION_KEY] === true;
}

/** A default scene title is not a caption. */
function isPlaceholderLine(line: string): boolean {
  return /^Scene \d+$/.test(line);
}

function sameMotion(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** The enter motion stored on this page's caption. */
export function captionMotionOf(page: ProjectPage): ReelCaptionMotion {
  const caption = (page.canvasData.objects || []).find(isReelCaption);
  const anim = caption?.craftaraAnim as LayerAnimation | undefined;
  const preset = anim?.in?.preset;
  if (preset === 'fade-in') return 'fade';
  if (preset === 'slide-in') return 'slide';
  if (preset === 'rise') return 'rise';
  return 'none';
}

function motionAnim(motion: ReelCaptionMotion): LayerAnimation | undefined {
  if (motion === 'none') return undefined;
  const preset = MOTION_PRESET[motion];
  const def = presetDef(preset);
  const step = {
    preset,
    durationMs: def?.defaultDurationMs ?? 600,
    delayMs: 0,
    easing: def?.defaultEasing ?? 'ease-out',
    intensity: 1,
    ...(def?.supportsDirection ? { direction: 'down' as const } : {}),
  };
  return { v: 1, in: step };
}

function captionObject(
  text: string,
  width: number,
  height: number
): Record<string, unknown> {
  const fontSize = Math.round(Math.min(64, Math.max(18, height * 0.03)));
  return {
    type: 'textbox',
    [REEL_CAPTION_KEY]: true,
    originX: 'center',
    originY: 'bottom',
    left: Math.round(width / 2),
    top: Math.round(height * (1 - reelCaptionInset())),
    width: Math.round(width * 0.84),
    text,
    fontSize,
    fontWeight: '700',
    fontFamily: 'Arial',
    fill: '#ffffff',
    textAlign: 'center',
    shadow: { color: 'rgba(0,0,0,0.72)', blur: 8, offsetX: 0, offsetY: 2 },
    selectable: false,
    evented: false,
  };
}

/**
 * Put `line` on the page as the caption. A blank line or a "Scene N" title
 * removes it. The same page is returned when nothing changed.
 */
export function withReelCaption(page: ProjectPage, line: string): ProjectPage {
  const text = line.trim();
  const show = text.length > 0 && !isPlaceholderLine(text);
  const objects = page.canvasData.objects || [];
  const current = objects.find(isReelCaption);
  const rest = objects.filter((obj) => !isReelCaption(obj));
  if (!show) {
    if (!current) return page;
    return { ...page, canvasData: { ...page.canvasData, objects: rest } };
  }
  const width = page.canvasData.width || 1080;
  const height = page.canvasData.height || 1920;
  const next = captionObject(text, width, height);
  if (current?.craftaraAnim) next.craftaraAnim = current.craftaraAnim;
  if (typeof current?.left === 'number') next.left = current.left;
  if (typeof current?.top === 'number') next.top = current.top;
  if (
    current &&
    current.text === next.text &&
    current.left === next.left &&
    current.top === next.top &&
    current.width === next.width &&
    sameMotion(current.craftaraAnim, next.craftaraAnim)
  ) {
    return page;
  }
  return { ...page, canvasData: { ...page.canvasData, objects: [...rest, next] } };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Where the caption sits, as fractions of the page. `y` is the bottom of the words. */
export function reelCaptionPlace(page: ProjectPage): { text: string; x: number; y: number } | null {
  const caption = (page.canvasData.objects || []).find(isReelCaption);
  const text = typeof caption?.text === 'string' ? caption.text.trim() : '';
  if (!caption || !text) return null;
  const width = page.canvasData.width || 1080;
  const height = page.canvasData.height || 1920;
  const left = typeof caption.left === 'number' ? caption.left : width / 2;
  const top = typeof caption.top === 'number' ? caption.top : height * (1 - reelCaptionInset());
  return { text, x: left / width, y: top / height };
}

/** Keep a dragged caption on the picture. The same page is returned when nothing moved. */
export function moveReelCaption(page: ProjectPage, left: number, top: number): ProjectPage {
  const width = page.canvasData.width || 1080;
  const height = page.canvasData.height || 1920;
  const objects = page.canvasData.objects || [];
  if (!objects.some(isReelCaption)) return page;
  const nextLeft = Math.round(clamp(left, width * 0.08, width * 0.92));
  const nextTop = Math.round(clamp(top, height * 0.08, height * 0.96));
  let changed = false;
  const next = objects.map((obj) => {
    if (!isReelCaption(obj)) return obj;
    if (obj.left === nextLeft && obj.top === nextTop) return obj;
    changed = true;
    return { ...obj, left: nextLeft, top: nextTop };
  });
  if (!changed) return page;
  return { ...page, canvasData: { ...page.canvasData, objects: next } };
}

/** Set how the caption enters. A page with no caption is left unchanged. */
export function withCaptionMotion(page: ProjectPage, motion: ReelCaptionMotion): ProjectPage {
  const objects = page.canvasData.objects || [];
  const caption = objects.find(isReelCaption);
  if (!caption) return page;
  if (captionMotionOf(page) === motion) return page;
  const anim = motionAnim(motion);
  const next = objects.map((obj) => {
    if (!isReelCaption(obj)) return obj;
    const copy = { ...obj };
    if (anim) copy.craftaraAnim = anim;
    else delete copy.craftaraAnim;
    return copy;
  });
  return { ...page, canvasData: { ...page.canvasData, objects: next } };
}
